import React from "react";
import { useNavigate } from "react-router-dom";
import {
  Stack,
  Text,
  Heading,
  Link,
  Box,
  UnorderedList,
  ListItem,
} from "@chakra-ui/react";
import { StyledButton } from "../styled-components/StyledButton";

export const TermsPage = () => {
  document.title = "Terms of Service | SB Prop Bets";
  const navigate = useNavigate();

  return (
    <Stack spacing={4} align="center" pb="40px" px="24px">
      <Box paddingTop="40px">
        <StyledButton
          onClick={() => navigate("/home")}
          size="sm"
          useDarkMode
          colorScheme="brandColorScheme"
          variant="ghost"
        >
          &larr; Back to Home
        </StyledButton>
      </Box>

      <Heading fontSize={["2xl", "3xl"]} pt="20px">
        Terms of Service
      </Heading>

      <Text fontSize="sm" color="gray.400">
        Last updated: February 1, 2025
      </Text>

      <Stack spacing={6} maxWidth="2xl" align="start">
        <Text>
          Welcome to SB Prop Bets. By accessing or using our website at sbpropbets.com
          ("Service"), you agree to be bound by these Terms of Service ("Terms").
          Please read them carefully.
        </Text>

        <Box>
          <Heading fontSize="lg" mb={2}>
            Description of Service
          </Heading>
          <Text>
            SB Prop Bets is a free platform that allows users to create and participate
            in prop bet pools for sporting events, primarily the Super Bowl. Users can
            create groups, share links with friends, and submit their predictions. The
            Service tracks scores and displays results in real-time during events.
          </Text>
        </Box>

        <Box>
          <Heading fontSize="lg" mb={2}>
            No Gambling or Money Handling
          </Heading>
          <Text mb={2}>
            <strong>
              SB Prop Bets does not process, handle, or facilitate any financial
              transactions.
            </strong>{" "}
            We do not accept payments, entry fees, or prize money. Any monetary
            arrangements between users (such as entry fees or prize pools) are:
          </Text>
          <UnorderedList spacing={1} pl={4}>
            <ListItem>Entirely between the participants</ListItem>
            <ListItem>Not facilitated, endorsed, or guaranteed by SB Prop Bets</ListItem>
            <ListItem>The sole responsibility of the users involved</ListItem>
          </UnorderedList>
          <Text mt={2}>
            SB Prop Bets is intended for entertainment purposes only. We are not a
            gambling service and do not promote illegal gambling.
          </Text>
        </Box>

        <Box>
          <Heading fontSize="lg" mb={2}>
            User Responsibilities
          </Heading>
          <Text mb={2}>By using the Service, you agree to:</Text>
          <UnorderedList spacing={1} pl={4}>
            <ListItem>Provide accurate information when creating groups or entries</ListItem>
            <ListItem>Use the Service only for lawful purposes</ListItem>
            <ListItem>Not use the Service to harass, abuse, or harm others</ListItem>
            <ListItem>Not attempt to interfere with or disrupt the Service</ListItem>
            <ListItem>Comply with all applicable laws in your jurisdiction</ListItem>
          </UnorderedList>
        </Box>

        <Box>
          <Heading fontSize="lg" mb={2}>
            Intellectual Property
          </Heading>
          <Text>
            The Service and its original content, features, and functionality are owned
            by SB Prop Bets and are protected by copyright, trademark, and other
            intellectual property laws. You may not copy, modify, distribute, or
            create derivative works based on the Service without our express permission.
          </Text>
        </Box>

        <Box>
          <Heading fontSize="lg" mb={2}>
            User Content
          </Heading>
          <Text>
            You retain ownership of any content you submit to the Service (such as
            group names and entry names). By submitting content, you grant us a
            non-exclusive, royalty-free license to use, display, and store that
            content as necessary to provide the Service.
          </Text>
        </Box>

        <Box>
          <Heading fontSize="lg" mb={2}>
            Disclaimer of Warranties
          </Heading>
          <Text>
            The Service is provided "as is" and "as available" without warranties of
            any kind, either express or implied. We do not guarantee that the Service
            will be uninterrupted, secure, or error-free. We make no warranties about
            the accuracy or reliability of results displayed on the Service.
          </Text>
        </Box>

        <Box>
          <Heading fontSize="lg" mb={2}>
            Limitation of Liability
          </Heading>
          <Text>
            To the fullest extent permitted by law, SB Prop Bets shall not be liable
            for any indirect, incidental, special, consequential, or punitive damages,
            including but not limited to loss of profits, data, or other intangible
            losses, resulting from your use of the Service. This includes any disputes
            between users regarding entry fees, prizes, or other monetary arrangements.
          </Text>
        </Box>

        <Box>
          <Heading fontSize="lg" mb={2}>
            Indemnification
          </Heading>
          <Text>
            You agree to indemnify and hold harmless SB Prop Bets and its operators
            from any claims, damages, or expenses arising from your use of the Service
            or violation of these Terms.
          </Text>
        </Box>

        <Box>
          <Heading fontSize="lg" mb={2}>
            Termination
          </Heading>
          <Text>
            We reserve the right to terminate or suspend your access to the Service
            at any time, without notice, for any reason, including violation of these
            Terms.
          </Text>
        </Box>

        <Box>
          <Heading fontSize="lg" mb={2}>
            Changes to Terms
          </Heading>
          <Text>
            We may modify these Terms at any time. We will notify users of significant
            changes by posting the updated Terms on this page. Your continued use of
            the Service after changes constitutes acceptance of the new Terms.
          </Text>
        </Box>

        <Box>
          <Heading fontSize="lg" mb={2}>
            Contact Us
          </Heading>
          <Text>
            If you have any questions about these Terms, please contact us at{" "}
            <Link color="brandColorScheme.300" href="mailto:hello@sbpropbets.com">
              hello@sbpropbets.com
            </Link>.
          </Text>
        </Box>
      </Stack>
    </Stack>
  );
};
